// Platform classification: which application family does a probe
// belong to? Mirrors `Platform` in `libs/backends/v2-uptime-data/
// src/platform.go` so the backend rollups and the frontend group
// agree. Order in `UPTIME_PLATFORM_ORDER` is the order the sidebar
// renders platform sections, top-to-bottom.
export const UPTIME_PLATFORM_PLACEPOINT_V2 = "placepoint-v2"
export const UPTIME_PLATFORM_PLACEPOINT_V1 = "placepoint-v1"
export const UPTIME_PLATFORM_PROPCLOUD = "propcloud"
export const UPTIME_PLATFORM_NEWSECMAPS = "newsecmaps"

export const UPTIME_PLATFORM_ORDER = [
  UPTIME_PLATFORM_PLACEPOINT_V2,
  UPTIME_PLATFORM_PLACEPOINT_V1,
  UPTIME_PLATFORM_PROPCLOUD,
  UPTIME_PLATFORM_NEWSECMAPS,
] as const

export type UptimePlatform = (typeof UPTIME_PLATFORM_ORDER)[number]

export const UPTIME_PLATFORM_LABELS: Record<UptimePlatform, string> = {
  [UPTIME_PLATFORM_PLACEPOINT_V2]: "Placepoint",
  [UPTIME_PLATFORM_PLACEPOINT_V1]: "Placepoint v1",
  [UPTIME_PLATFORM_PROPCLOUD]: "Propcloud",
  [UPTIME_PLATFORM_NEWSECMAPS]: "Newsecmaps",
}

export const UPTIME_PLATFORM_DESCRIPTIONS: Record<UptimePlatform, string> = {
  [UPTIME_PLATFORM_PLACEPOINT_V2]:
    "The current Placepoint platform: apps, GIS, and customer experiences.",
  [UPTIME_PLATFORM_PLACEPOINT_V1]:
    "The legacy Placepoint platform still serving a small number of customers.",
  [UPTIME_PLATFORM_PROPCLOUD]:
    "Propcloud apps, reports, and partner-facing experiences.",
  [UPTIME_PLATFORM_NEWSECMAPS]: "Newsecmaps API and supporting services.",
}

// Source-string constants for the partner-platform probes the
// uptime monitor emits via its `external` runner. Identifies a row
// at the per-target level when grouping by platform; mirrors
// `sourceExternal*` in `v2-uptime-data/src/platform.go`. Sub-buckets
// (e.g. `.deps`) carry the same platform but separate the partner's
// own product endpoints from third-party services they depend on.
export const UPTIME_SOURCE_EXTERNAL_PROPCLOUD = "external.propcloud"
export const UPTIME_SOURCE_EXTERNAL_NEWSECMAPS = "external.newsecmaps"
export const UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_DEPS = "external.newsecmaps.deps"
// Cloud-provider sub-buckets: each platform's hyperscaler dependency
// (Azure for Placepoint, AWS for Propcloud, GCP for Newsecmaps).
// Stored as its own Source so the per-platform card can list "Azure
// · Entra ID" alongside "Azure · Graph" without conflating those
// rows with the platform's own application probes.
export const UPTIME_SOURCE_EXTERNAL_PLACEPOINT_V2_CLOUD =
  "external.placepoint-v2.cloud"
export const UPTIME_SOURCE_EXTERNAL_PROPCLOUD_CLOUD = "external.propcloud.cloud"
export const UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_CLOUD =
  "external.newsecmaps.cloud"

// Display order + human labels for the Source column coming back from
// backoffice.Get_Backoffice_UptimeTargets. Anything not in this map
// falls back to the raw Source string via uptime.utils.ts
// (getCategoryLabel). Keep the order stable -- the uptime board
// renders categories in the order they appear here within each
// platform.
export const UPTIME_CATEGORY_ORDER = [
  "app.release",
  "app.staging",
  "app.next",
  "app.lkg",
  "basemap",
  "navlayer",
  "maplayer",
  UPTIME_SOURCE_EXTERNAL_PLACEPOINT_V2_CLOUD,
  UPTIME_SOURCE_EXTERNAL_PROPCLOUD,
  UPTIME_SOURCE_EXTERNAL_PROPCLOUD_CLOUD,
  UPTIME_SOURCE_EXTERNAL_NEWSECMAPS,
  UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_DEPS,
  UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_CLOUD,
] as const

export const UPTIME_CATEGORY_LABELS: Record<string, string> = {
  "app.release": "Production Apps",
  "app.staging": "Staging Apps",
  "app.next": "Next Apps",
  "app.lkg": "Last-Known-Good Apps",
  basemap: "GIS Basemaps",
  navlayer: "GIS Navigation Layers",
  maplayer: "GIS Map Layers",
  [UPTIME_SOURCE_EXTERNAL_PROPCLOUD]: "Propcloud Apps",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS]: "Newsecmaps API",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_DEPS]: "Map Service Dependencies",
  [UPTIME_SOURCE_EXTERNAL_PLACEPOINT_V2_CLOUD]: "Microsoft Azure",
  [UPTIME_SOURCE_EXTERNAL_PROPCLOUD_CLOUD]: "Amazon Web Services",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_CLOUD]: "Google Cloud Platform",
}

export const UPTIME_CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "app.release": "Services running in production (release slot).",
  "app.staging": "Services running in the staging slot.",
  "app.next": "Services deployed from the next branch.",
  "app.lkg": "Services running last-known-good builds.",
  basemap: "Base map tile services shown underneath content layers.",
  navlayer: "Top-level layer groups used in navigation menus.",
  maplayer: "Individual layers rendered on the map.",
  [UPTIME_SOURCE_EXTERNAL_PROPCLOUD]:
    "Propcloud customer-facing apps probed via plain HTTP.",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS]:
    "Newsecmaps health endpoint probed via plain HTTP.",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_DEPS]:
    "Third-party WMS / ArcGIS services Newsecmaps depends on.",
  [UPTIME_SOURCE_EXTERNAL_PLACEPOINT_V2_CLOUD]:
    "Azure services Placepoint runs on (auth, resource manager, …).",
  [UPTIME_SOURCE_EXTERNAL_PROPCLOUD_CLOUD]:
    "AWS services Propcloud runs on (sign-in, S3, edge, …).",
  [UPTIME_SOURCE_EXTERNAL_NEWSECMAPS_CLOUD]:
    "GCP / Firebase services Newsecmaps runs on.",
}

// The detail-view chart range switcher. Values map 1:1 to the
// graphql UptimeRange enum -- keep them in sync.
export const UPTIME_RANGE_OPTIONS = [
  { value: "HOUR", label: "Hour" },
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
] as const

export type UptimeRangeValue = (typeof UPTIME_RANGE_OPTIONS)[number]["value"]
